import { usePermissions } from '@/modules/common/hooks/use-permissions';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';

import { ApprovalStatus, ApprovalStepStatus, TimeOffRequest } from '../types/time-off-request';
import { TimeOffStatus } from '../types/time-off-status';
import { useCurrentApproverEntry } from './use-current-approver-decision';

interface TimeOffReviewActions {
  /** Whether the approve action should be offered. */
  canShowApprove: boolean;
  /** Whether the decline action should be offered. */
  canShowDecline: boolean;
  /** True when at least one action is available (nothing to render otherwise). */
  hasAnyAction: boolean;
  /** True when the current user is queued behind an earlier approver whose turn it isn't yet. */
  isBlockedByEarlierApprover: boolean;
}

/**
 * Single source of truth for whether the current user can act on a time-off
 * request, shared by the approvals row actions cell and the approve-mode header
 * buttons.
 *
 * Actions are available only while the request is PENDING + UPCOMING and the
 * user holds `TimeOffsApprove`. Within that window the buttons reflect THIS
 * approver's own decision (a request can have multiple approvers): the offered
 * action is always the opposite of their last decision, so they can reverse it.
 * A queued approver whose earlier steps haven't decided yet is blocked instead
 * of being offered actions that would 403.
 */
export const useTimeOffReviewActions = (request: TimeOffRequest): TimeOffReviewActions => {
  const { hasAnyPermission } = usePermissions();
  const approverEntry = useCurrentApproverEntry(request);

  const canApprove = hasAnyPermission([OpsiumPolicy.TimeOffsApprove]);
  // A still-PENDING request is reviewable regardless of whether its dates are
  // upcoming or already past — managers routinely approve/decline late requests.
  const isActionable = request.status === TimeOffStatus.Pending;
  const isBlockedByEarlierApprover = approverEntry?.stepStatus === ApprovalStepStatus.Blocked;

  const showApprove = approverEntry?.status !== ApprovalStatus.Approved;
  const showDecline = approverEntry?.status !== ApprovalStatus.Rejected;

  const canShowApprove = canApprove && isActionable && showApprove && !isBlockedByEarlierApprover;
  const canShowDecline = canApprove && isActionable && showDecline && !isBlockedByEarlierApprover;

  return {
    canShowApprove,
    canShowDecline,
    hasAnyAction: canShowApprove || canShowDecline,
    isBlockedByEarlierApprover: canApprove && isActionable && isBlockedByEarlierApprover,
  };
};
